import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import useAnchoredPosition from "./useAnchoredPosition";

// Drop-in replacement for a form `<select>`.
//
// Swap the tag and keep everything else — the same `<option>` children, the same
// `name` / `value` / `className` / `required`, and the same onChange handler
// (it receives an event-shaped object, so `e.target.value` and `e.target.name`
// both still work).
//
// Why: the native control renders its list with the OS, sized to the longest
// option. Court and case-type names in this app run to a full sentence, so that
// list spilled across the window. Here the popup lives in a portal with a fixed,
// viewport-clamped box, and long labels wrap inside it.
//
// A real (transparent, non-tab-reachable) <select> stays mounted underneath so
// `required` still blocks submission and the field still posts with its form —
// several court-search pages depend on that.

const textOf = (node) =>
  React.Children.toArray(node)
    .map((c) => {
      if (typeof c === "string" || typeof c === "number") return String(c);
      return React.isValidElement(c) ? textOf(c.props.children) : "";
    })
    .join("");

const flattenOptions = (nodes, out = [], group = null) => {
  React.Children.forEach(nodes, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === "optgroup") {
      flattenOptions(child.props.children, out, child.props.label);
    } else if (child.type === "option") {
      const label = textOf(child.props.children);
      out.push({
        value: child.props.value !== undefined ? String(child.props.value) : label,
        label,
        disabled: !!child.props.disabled,
        group,
      });
    }
  });
  return out;
};

export default function SelectMenu({
  value,
  onChange,
  name,
  children,
  className = "",
  required = false,
  disabled = false,
  // SelectShell already draws a chevron/spinner for the court-search forms, so
  // those call sites turn ours off rather than stacking two.
  chevron = true,
  searchable,
  placeholder = "Select",
  "aria-label": ariaLabel,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const options = useMemo(() => flattenOptions(children), [children]);
  const current = String(value ?? "");
  const selected = options.find((o) => o.value === current);

  // The popup matches the field's width — these are full-width form controls,
  // not chips, so there's no useful narrower size.
  const pos = useAnchoredPosition(open, triggerRef, {
    minWidth: 220,
    maxWidth: 9999,
    maxHeight: 320,
  });

  const canSearch = searchable ?? options.length > 8;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    const idx = options.findIndex((o) => o.value === current);
    setActiveIdx(idx >= 0 ? idx : 0);
    setOpen(true);
    if (canSearch) requestAnimationFrame(() => searchRef.current?.focus());
  };

  const commit = (opt) => {
    if (opt.disabled) return;
    setOpen(false);
    triggerRef.current?.focus();
    // Event-shaped so existing `handleInputChange(e)` handlers keep working.
    onChange?.({
      target: { name, value: opt.value },
      currentTarget: { name, value: opt.value },
      preventDefault: () => {},
      stopPropagation: () => {},
    });
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = visible[activeIdx];
      if (opt) commit(opt);
    }
  };

  const label = selected ? selected.label : placeholder;
  const isEmpty = !selected || selected.value === "";

  // The wrapper only exists to anchor the hidden native select over the button,
  // so it must not change the field's box: it takes the caller's full width when
  // the field had one, and otherwise shrinks to the control like the old inline
  // <select> did.
  const wrapperCls = `relative inline-block align-middle ${
    /\bw-full\b/.test(className) ? "w-full" : "max-w-full"
  }`;

  return (
    <span className={wrapperCls}>
      {/* Kept for form semantics only: `required` validation, form data, and
          anything reading the field by name. Transparent and not tab-reachable,
          but still focusable so Chrome can anchor its validation bubble here. */}
      <select
        name={name}
        value={current}
        onChange={(e) => onChange?.(e)}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      >
        {children}
      </select>

      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        title={isEmpty ? undefined : label}
        // `select-menu-trigger` is a styling hook for call sites whose CSS
        // targeted the old element (e.g. `.ticket-field select`); the visible
        // control is a <button> now, so those rules need a way to reach it.
        className={`select-menu-trigger relative flex items-center justify-between gap-2 text-left ${className} ${
          isEmpty ? "text-slate-400" : ""
        }`}
      >
        <span className="truncate">{label}</span>
        {chevron && (
          <ChevronDown
            className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="listbox"
              aria-label={ariaLabel}
              style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width }}
              // Above every modal overlay in the app (the highest is z-[9999]),
              // since these fields are used inside those modals.
              className="fixed z-[10000] flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl"
              onKeyDown={onKeyDown}
            >
              {canSearch && (
                <div className="relative shrink-0 border-b border-slate-100 p-2">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActiveIdx(0);
                    }}
                    placeholder="Search..."
                    className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400/40"
                  />
                </div>
              )}

              <div
                className="overflow-y-auto overscroll-contain py-1"
                style={{ maxHeight: pos.maxHeight - (canSearch ? 48 : 0) }}
              >
                {visible.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-slate-400">No matches</p>
                ) : (
                  visible.map((opt, i) => {
                    const isSel = opt.value === current;
                    const header = opt.group && visible[i - 1]?.group !== opt.group;
                    return (
                      <React.Fragment key={`${opt.group || ""}-${opt.value}-${i}`}>
                        {header && (
                          <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            {opt.group}
                          </p>
                        )}
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSel}
                          disabled={opt.disabled}
                          title={opt.label}
                          onMouseEnter={() => setActiveIdx(i)}
                          onClick={() => commit(opt)}
                          className={`flex w-full items-start gap-2 px-3 py-1.5 text-left text-xs leading-snug transition-colors ${
                            i === activeIdx && !opt.disabled ? "bg-yellow-50" : ""
                          } ${
                            opt.disabled
                              ? "cursor-not-allowed text-slate-300"
                              : isSel
                              ? "font-semibold text-slate-900"
                              : "text-slate-700"
                          }`}
                        >
                          <Check
                            className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-600 ${isSel ? "" : "invisible"}`}
                          />
                          {/* Long court / case-type names stay inside the panel:
                              wrap to at most 3 lines, then ellipsize. */}
                          <span className="line-clamp-3 break-words">{opt.label}</span>
                        </button>
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
}
